"use client";

import { useEffect, useRef } from "react";

import Image from "next/image";

import { getTemplateAssetCdnUrl, getTemplateCategory, TemplateDefinition } from "@assets/templates";

import { CarouselConfig, SLOT_STYLE } from "./config";

type SlotProps = {
  template: TemplateDefinition;
  /** Whether the active video should play (driven by selectedScrollSnap in the parent). */
  isActive: boolean;
  config: CarouselConfig;
};

/**
 * One thumbnail slot. Pure presentation — the parent's scroll-progress tween
 * mutates `scale`/`filter`/`boxShadow`/halo `opacity` via the `data-tween-*`
 * hooks below.
 *
 * Poster + halo go through `next/image` (not a `<video poster>` attr or a
 * CSS `background-image`) because both of those fetch in no-cors mode and
 * poison the cache for the poster URL with a headerless response. The
 * `<video>` stays mounted across the slot's lifetime; toggling its opacity
 * by `isActive` is gentler than mount/unmount churn for `play()`.
 */
const Slot = ({ template, isActive, config }: SlotProps) => {
  const category = getTemplateCategory(template);
  const posterUrl = getTemplateAssetCdnUrl(template.thumbnailUuid);
  const videoAsset = template.assets.find((a) => a.mime.startsWith("video/"));
  const videoUrl = videoAsset ? getTemplateAssetCdnUrl(videoAsset.uuid) : null;
  const videoRef = useRef<HTMLVideoElement>(null);
  const showVideo = category === "video" && videoUrl !== null;

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (isActive) {
      // Seeking before metadata can throw; the play() promise can reject for
      // autoplay-policy reasons. Both are recoverable — poster stays visible.
      try {
        video.currentTime = 0;
      } catch {}
      void video.play().catch(() => {});
    } else {
      video.pause();
    }
  }, [isActive]);

  return (
    <div
      className="relative shrink-0"
      style={{ width: `${config.featuredSize}px`, height: `${config.featuredSize}px` }}
    >
      {/* Image-colored halo behind the active thumb. No `overflow-hidden`
          here (Safari clips the `filter: blur` paint to the box otherwise).
          `will-change: filter, transform` promotes the halo to its own
          compositor layer so Safari doesn't subordinate it to the video
          element's hardware-composited layer when video is playing. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10"
        data-tween-halo
        style={{
          transform: `scale(${SLOT_STYLE.haloScale})`,
          filter: SLOT_STYLE.haloFilter,
          opacity: 0,
          willChange: "filter, transform",
        }}
      >
        <Image alt="" className="object-cover" fill sizes={`${config.featuredSize}px`} src={posterUrl} />
      </div>

      {/* Thumbnail. `::after` pseudo draws the inset border above the image so
          it stays sharp under scale; color comes from the CSS custom property
          (Tailwind's `after:border` defaults to currentColor otherwise). */}
      <div
        className="relative h-full w-full overflow-hidden rounded-2xl will-change-transform after:pointer-events-none after:absolute after:inset-0 after:rounded-[inherit] after:border after:border-[var(--slot-border-color)] after:content-['']"
        data-tween-thumb
        style={{
          transformOrigin: "center",
          ["--slot-border-color" as string]: SLOT_STYLE.innerBorderColor,
        }}
      >
        <Image alt={template.title} className="object-cover" fill sizes={`${config.featuredSize}px`} src={posterUrl} />
        {showVideo && videoUrl && (
          <video
            aria-hidden="true"
            className="absolute inset-0 size-full object-cover transition-opacity duration-200"
            // CORS-mode fetch so the cache entry matches the agent-output player's reads.
            crossOrigin="anonymous"
            disablePictureInPicture
            disableRemotePlayback
            loop
            muted
            playsInline
            preload="metadata"
            ref={videoRef}
            src={videoUrl}
            style={{ opacity: isActive ? 1 : 0 }}
          />
        )}
      </div>
    </div>
  );
};

export default Slot;
